'use client';

import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { CaretUp } from 'assets/icons';
import { jetbrainsMono } from 'utils/fonts';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import { Container } from '../../Container';
import { useFormatLink } from '../../CustomLink';
import styles from '../MarketingBanner.module.css';
import { omitsProductName } from './regions';

const Caret = ({ small }: { small?: boolean }) => (
  <CaretUp
    style={
      small
        ? {
            transform: 'rotate(90deg)',
            height: '8px',
            opacity: 0.85,
            marginLeft: '4px',
          }
        : { transform: 'rotate(90deg)' }
    }
    width={small ? undefined : 14}
    height={small ? undefined : 10}
  />
);

const LinkTile = ({
  href,
  className,
  gtmType = 'marketing banner',
  children,
}: {
  href: string;
  className: string;
  gtmType?: string;
  children: React.ReactNode;
}) => {
  const router = useRouter();
  const format = useFormatLink();
  return (
    <div
      className={`${className} gtm`}
      onClick={() => router.push(format(href) as string)}
      aria-hidden="true"
      data-buttontype={gtmType}
    >
      {children}
    </div>
  );
};

export const ShippingTile = () => {
  const t = useTranslations('common');
  const { region, flag, countryName } = useRegionLocale();
  if (!countryName) return null;

  const isGlobal = region === 'GLOBAL';
  const namesProduct = isGlobal || !omitsProductName(region);

  return (
    <div
      className={`${styles.shippingBanner} gtm`}
      aria-hidden="true"
      data-buttontype="marketing banner"
    >
      <Container>
        {t('common.marketingBanner.shipping')}{' '}
        {namesProduct && t('common.marketingBanner.productName')}{' '}
        {!isGlobal && (
          <>
            {t('common.marketingBanner.toCountry', { country: countryName })}{' '}
            {flag && (
              <Image
                src={flag}
                alt={t('common.marketingBanner.countryFlagAlt', {
                  country: region,
                })}
                width={16}
                height={16}
                style={{
                  borderRadius: '50%',
                  display: 'inline-block',
                  verticalAlign: 'middle',
                }}
              />
            )}
          </>
        )}
      </Container>
    </div>
  );
};

export const M2LiveTile = () => {
  const t = useTranslations('common');
  return (
    <LinkTile href="/pricing" className={styles.m2LiveBanner}>
      <Container>
        {t('common.marketingBanner.m2Live')} <Caret />
      </Container>
    </LinkTile>
  );
};

const PerformanceLabTile = ({
  href,
  desktopKey,
  mobileKey,
}: {
  href: string;
  desktopKey: string;
  mobileKey: string;
}) => {
  const t = useTranslations('common');
  return (
    <LinkTile
      href={href}
      className={`${styles.performanceLabBanner} ${jetbrainsMono.className} ${jetbrainsMono.variable}`}
    >
      <span>
        <span className={styles.onlyDesktop}>
          {t(desktopKey)}{' '}
          <span className={styles.ctaText}>
            {t('common.marketingBanner.learnMore')}
          </span>
        </span>
        <span className={styles.onlyMobile}>{t(mobileKey)}</span>
        <span className={styles.ctaIcon}>
          <Caret small />
        </span>
      </span>
    </LinkTile>
  );
};

export const PerformanceLabMonsoonTile = () => (
  <PerformanceLabTile
    href="/performance-lab/buy"
    desktopKey="common.marketingBanner.plIndependenceDesktopBanner"
    mobileKey="common.marketingBanner.plIndependenceMobileBanner"
  />
);

export const PerformanceLabGlpTile = () => (
  <PerformanceLabTile
    href="/performance-lab/glp"
    desktopKey="common.marketingBanner.glpDesktopBanner"
    mobileKey="common.marketingBanner.glpMobileBanner"
  />
);

/**
 * Shared by SddTwoHourTile (IN) and SddFourHourAeTile (AE): same link target
 * and visual treatment (`styles.sddTwoHourBanner` is copy-agnostic — no text
 * baked into the class), only the copy keys differ.
 */
const SddDeliveryTile = ({
  href,
  desktopKey,
  mobileKey,
}: {
  href: string;
  desktopKey: string;
  mobileKey: string;
}) => {
  const t = useTranslations('common');
  return (
    <LinkTile href={href} className={styles.sddTwoHourBanner}>
      <span>
        <span className={styles.onlyDesktop}>{t(desktopKey)}</span>
        <span className={styles.onlyMobile}>{t(mobileKey)}</span>
        {/* currentColor: the strip is themed (dark text on light routes),
            and CaretUp's default fill is hardcoded white. */}
        <CaretUp
          fill="currentColor"
          width={11}
          height={8}
          style={{
            transform: 'rotate(90deg)',
            opacity: '0.85',
            marginLeft: '6px',
            verticalAlign: 'middle',
          }}
        />
      </span>
    </LinkTile>
  );
};

/**
 * 11 AM-7 PM is the two_hour order window (docs/feature-sdd-ndd-ist-windows.md).
 * Mobile drops the product name, never the select-cities qualifier.
 */
export const SddTwoHourTile = () => (
  <SddDeliveryTile
    href="/ring/buy"
    desktopKey="common.marketingBanner.sddTwoHourDesktopBanner"
    mobileKey="common.marketingBanner.sddTwoHourMobileBanner"
  />
);

/** UAE 4-hour delivery launch tile. */
export const SddFourHourAeTile = () => (
  <SddDeliveryTile
    href="/ring/buy"
    desktopKey="common.marketingBanner.sddFourHourAeDesktopBanner"
    mobileKey="common.marketingBanner.sddFourHourAeMobileBanner"
  />
);

/**
 * Pulsomics launch tile. `fill="currentColor"` on the caret is load-bearing:
 * CaretUp defaults to a hardcoded white fill, which is invisible on the
 * light-header routes this themed tile inverts to.
 */
export const PulsomicsTile = () => {
  const t = useTranslations('common');
  return (
    <LinkTile
      href="/pulsomics"
      className={styles.pulsomicsBanner}
      gtmType="marketing_banner_pulsomics"
    >
      <span>
        <span className={styles.onlyDesktop}>
          {t('common.marketingBanner.pulsomicsDesktop')}{' '}
          <span className={styles.ctaText}>
            {t('common.marketingBanner.learnMore')}
          </span>
        </span>
        <span className={styles.onlyMobile}>
          {t('common.marketingBanner.pulsomicsMobile')}
        </span>
        <span className={styles.ctaIcon}>
          <CaretUp
            fill="currentColor"
            width={11}
            height={8}
            style={{
              transform: 'rotate(90deg)',
              opacity: '0.85',
              marginLeft: '6px',
              verticalAlign: 'middle',
            }}
          />
        </span>
      </span>
    </LinkTile>
  );
};

export const TILES: Record<string, React.ComponentType> = {
  shipping: ShippingTile,
  'm2-live': M2LiveTile,
  'performance-lab-monsoon': PerformanceLabMonsoonTile,
  'performance-lab-glp': PerformanceLabGlpTile,
  'sdd-two-hour': SddTwoHourTile,
  'sdd-four-hour-ae': SddFourHourAeTile,
  pulsomics: PulsomicsTile,
};
